import { decryptData, encryptData } from '@/utils/encryption';

const DB_NAME = 'inovva-zustand';
const STORE_NAME = 'persist';
const DB_VERSION = 1;

let databasePromise;
const keyChains = new Map();

/** Serializes reads and writes for one key so a legacy migration cannot overwrite a newer save. */
function enqueue(name, task) {
  const previous = keyChains.get(name) || Promise.resolve();
  const run = previous.then(task, task);
  keyChains.set(
    name,
    run.then(
      () => undefined,
      () => undefined
    )
  );
  return run;
}

function canUseLocalStorage() {
  return typeof window !== 'undefined' && !!window.localStorage;
}

function readLocalStorage(name) {
  if (!canUseLocalStorage()) return null;
  try {
    return localStorage.getItem(name);
  } catch {
    return null;
  }
}

function removeLocalStorage(name) {
  if (!canUseLocalStorage()) return;
  try {
    localStorage.removeItem(name);
  } catch {
    // Ignore quota / privacy-mode failures. IndexedDB is the source of truth.
  }
}

function openDatabase() {
  if (typeof indexedDB === 'undefined') {
    return Promise.resolve(null);
  }

  if (!databasePromise) {
    databasePromise = new Promise((resolve) => {
      let request;
      try {
        request = indexedDB.open(DB_NAME, DB_VERSION);
      } catch (error) {
        console.error('[indexeddb] Failed to open database:', error);
        databasePromise = null;
        resolve(null);
        return;
      }

      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME);
        }
      };

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => {
        console.error('[indexeddb] Failed to open database:', request.error);
        databasePromise = null;
        resolve(null);
      };
      request.onblocked = () => {
        console.error('[indexeddb] Database open blocked by another tab');
        databasePromise = null;
        resolve(null);
      };
    });
  }

  return databasePromise;
}

function runTransaction(mode, operation) {
  return openDatabase().then((db) => {
    if (!db) return { available: false, value: null };

    return new Promise((resolve) => {
      try {
        const tx = db.transaction(STORE_NAME, mode);
        const store = tx.objectStore(STORE_NAME);
        const request = operation(store);

        request.onsuccess = () =>
          resolve({ available: true, value: request.result ?? null });
        request.onerror = () => {
          console.error('[indexeddb] Request failed:', request.error);
          resolve({ available: false, value: null });
        };
      } catch (error) {
        console.error('[indexeddb] Transaction failed:', error);
        resolve({ available: false, value: null });
      }
    });
  });
}

function idbGet(name) {
  return runTransaction('readonly', (store) => store.get(name));
}

function idbSet(name, value) {
  return runTransaction('readwrite', (store) => store.put(value, name));
}

function idbDelete(name) {
  return runTransaction('readwrite', (store) => store.delete(name));
}

async function readEncrypted(name) {
  const stored = await idbGet(name);

  if (stored.available && stored.value != null) {
    return stored.value;
  }

  const legacy = readLocalStorage(name);
  if (legacy == null) return null;

  if (stored.available) {
    const migrated = await idbSet(name, legacy);
    if (migrated.available) {
      removeLocalStorage(name);
    }
  }

  return legacy;
}

async function writeEncrypted(storeName, name, encrypted) {
  const saved = await idbSet(name, encrypted);
  if (saved.available) {
    removeLocalStorage(name);
    return;
  }

  if (!canUseLocalStorage()) return;

  try {
    localStorage.setItem(name, encrypted);
  } catch (error) {
    if (error?.name === 'QuotaExceededError') {
      console.error(`[${storeName}] Storage quota exceeded. Retrying...`);
      try {
        localStorage.removeItem(name);
        localStorage.setItem(name, encrypted);
      } catch (retryError) {
        console.error(`[${storeName}] Failed to save after clearing:`, retryError);
      }
    } else {
      console.error(`[${storeName}] Error saving store:`, error);
    }
  }
}

function unwrapStoredValue(storeName, name, decrypted, ttl) {
  if (!ttl || !decrypted?.timestamp || !decrypted?.data) {
    if (ttl && decrypted && !decrypted.timestamp) {
      console.info(`[${storeName}] Migrating legacy data to TTL format`);
    }
    return decrypted;
  }

  const age = Date.now() - decrypted.timestamp;
  if (age > ttl) {
    const hoursOld = Math.round(age / 1000 / 60 / 60);
    console.warn(
      `[${storeName}] Data expired (${hoursOld}h old, TTL: ${Math.round(ttl / 1000 / 60 / 60)}h). Clearing.`
    );
    idbDelete(name);
    removeLocalStorage(name);
    return null;
  }

  return decrypted.data;
}

/**
 * Zustand persist adapter backed by IndexedDB.
 * Values stay encrypted. Optional TTL matches the previous localStorage adapter.
 * Existing localStorage entries are copied across on the first read.
 *
 * @param {string} storeName
 * @param {number} [ttl] Time to live in milliseconds. Omit to keep data until removed.
 */
export function createIndexedDbStorage(storeName, ttl) {
  return {
    getItem: (name) =>
      enqueue(name, async () => {
        try {
          const encrypted = await readEncrypted(name);
          if (!encrypted) return null;

          const decrypted = decryptData(encrypted);
          if (!decrypted) {
            console.warn(
              `[${storeName}] Failed to decrypt persisted data. Clearing...`
            );
            await idbDelete(name);
            removeLocalStorage(name);
            return null;
          }

          return unwrapStoredValue(storeName, name, decrypted, ttl);
        } catch (error) {
          console.error(`[${storeName}] Error reading persisted data:`, error);
          await idbDelete(name);
          removeLocalStorage(name);
          return null;
        }
      }),

    setItem: (name, value) =>
      enqueue(name, async () => {
        try {
          const payload = ttl ? { timestamp: Date.now(), data: value } : value;
          await writeEncrypted(storeName, name, encryptData(payload));
        } catch (error) {
          console.error(`[${storeName}] Error saving persisted data:`, error);
        }
      }),

    removeItem: (name) =>
      enqueue(name, async () => {
        try {
          await idbDelete(name);
          removeLocalStorage(name);
        } catch (error) {
          console.error(`[${storeName}] Error removing persisted data:`, error);
        }
      }),
  };
}

/** Removes persisted Zustand keys from IndexedDB and any leftover localStorage copy. */
export async function clearPersistedStores(names) {
  await Promise.all(
    names.map((name) =>
      enqueue(name, async () => {
        await idbDelete(name);
        removeLocalStorage(name);
      })
    )
  );
}
